import { Injectable } from '@angular/core';
import type { PermissionState } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import type { ActionPerformed, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { createLogger } from '@hunting-nature/shared-core/logging';
import type { PushPlatform } from '@hunting-nature/shared-core/interfaces/push-device-registration.interface';
import type { Observable } from 'rxjs';
import { defer, from, of, ReplaySubject, Subject, switchMap } from 'rxjs';

/**
 * Fallback channel for pushes that arrive without an explicit `android_channel_id`.
 * Must stay in sync with `default_notification_channel_id` in
 * android/app/src/main/res/values/strings.xml — the manifest points FCM at this id, and
 * Android 8+ silently drops notifications for a channel that was never created.
 */
export const PUSH_DEFAULT_CHANNEL_ID = 'default_channel';

/** Android channel that uses the custom notification sound asset. */
export const PUSH_CUSTOM_SOUND_CHANNEL_ID = 'secacam_channel';

/** Outcome of {@link PushNotificationService.enable$}. */
export type PushEnableResult = 'granted' | 'denied' | 'unsupported';

/**
 * Thin seam over `@capacitor/push-notifications`.
 *
 * Owns nothing but the plugin: permission handling, the Android channel and turning the
 * plugin's callback events into observables. Deciding *when* to register and what to do
 * with a token is the job of PushEffects.
 *
 * Every entry point is a no-op on the web builds — the plugin has no web implementation,
 * and the `*-web` configurations are served from Firebase Hosting.
 */
@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly tokenSubject = new ReplaySubject<string>(1);
  private readonly registrationErrorSubject = new Subject<unknown>();
  private readonly notificationReceivedSubject = new Subject<PushNotificationSchema>();
  private readonly notificationActionPerformedSubject = new Subject<ActionPerformed>();

  private listenersAttached = false;
  private readonly log = createLogger('PushNotificationService');

  /**
   * The native handle. Replays, because the plugin can emit `registration` before any
   * effect subscribes — and again later when the OS rotates the token.
   */
  public readonly token$: Observable<string> = this.tokenSubject.asObservable();
  public readonly registrationError$: Observable<unknown> = this.registrationErrorSubject.asObservable();
  public readonly notificationReceived$: Observable<PushNotificationSchema> = this.notificationReceivedSubject.asObservable();
  public readonly notificationActionPerformed$: Observable<ActionPerformed> = this.notificationActionPerformedSubject.asObservable();

  /**
   * Attaches the native listeners without triggering permission prompts or registration.
   *
   * This is used on app startup so a notification tap that launched the app from a killed
   * state is not lost while auth/bootstrap is still in progress.
   */
  public async prepare(): Promise<void> {
    if (!this.isSupported) {
      return;
    }

    await this.attachListeners();
  }

  public get isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Azure Notification Hub platform name for the current device, or null on web:
   * `apns` (Apple Push Notification service) on iOS, `fcmv1` (Firebase Cloud Messaging,
   * HTTP v1) on Android. See PushPlatform for what each handle contains.
   */
  public get platform(): PushPlatform | null {
    if (!this.isSupported) {
      return null;
    }
    return Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcmv1';
  }

  /**
   * Attaches listeners, ensures the Android channel exists, resolves the OS permission
   * and — when granted — asks the OS for a token.
   *
   * Resolving `granted` only means the OS said yes; the token arrives asynchronously on
   * {@link token$}. On iOS that round-trip goes through APNs and can take a few seconds,
   * or never complete if the device is offline.
   */
  public enable$(): Observable<PushEnableResult> {
    if (!this.isSupported) {
      return of('unsupported' as const);
    }

    return defer(async () => {
      await this.prepare();
      await this.ensureAndroidChannels();

      // checkPermissions() first: requestPermissions() re-prompts only on the very first
      // call, so a user who denied earlier would otherwise silently look like 'prompt'.
      let status = await PushNotifications.checkPermissions();
      if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
        status = await PushNotifications.requestPermissions();
      }

      if (status.receive !== 'granted') {
        return 'denied' as const;
      }

      await PushNotifications.register();
      return 'granted' as const;
    });
  }

  /**
   * Drops the OS-level registration (deletes the FCM token on Android, unregisters from
   * APNs on iOS). Removing the installation from the hub is a separate, backend-side
   * concern — see PushEffects' logout handling.
   */
  public unregister$(): Observable<void> {
    if (!this.isSupported) {
      return of(void 0);
    }
    return from(PushNotifications.unregister());
  }

  /** Clears the notification centre and the iOS badge. */
  public removeAllDelivered$(): Observable<void> {
    if (!this.isSupported) {
      return of(void 0);
    }
    return from(PushNotifications.removeAllDeliveredNotifications());
  }

  public currentPermission$(): Observable<PermissionState> {
    if (!this.isSupported) {
      return of('denied' as PermissionState);
    }
    return from(PushNotifications.checkPermissions()).pipe(switchMap(status => of(status.receive)));
  }

  /**
   * Idempotent — `enable$()` may be called again on every login, and Capacitor keeps
   * every listener that was ever added, so re-attaching would multiply each event.
   */
  private async attachListeners(): Promise<void> {
    if (this.listenersAttached) {
      return;
    }
    // Set before awaiting: addListener is async, and two concurrent enable$() calls would
    // otherwise both pass the guard and register a duplicate set.
    this.listenersAttached = true;

    await PushNotifications.addListener('registration', (token: Token) => {
      this.tokenSubject.next(token.value);
    });

    await PushNotifications.addListener('registrationError', (error: unknown) => {
      this.log.error('registrationError', { error });
      this.registrationErrorSubject.next(error);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.notificationReceivedSubject.next(notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      this.notificationActionPerformedSubject.next(action);
    });
  }

  /**
   * Android-only. The plugin's createChannel is a no-op on iOS, but calling it there
   * still costs a bridge round-trip, so it is skipped explicitly.
   */
  private async ensureAndroidChannels(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    try {
      await PushNotifications.createChannel({
        id: PUSH_DEFAULT_CHANNEL_ID,
        name: 'Camera notifications',
        description: 'New images and alerts from your cameras',
        importance: 4, // IMPORTANCE_HIGH — heads-up display
        visibility: 1, // VISIBILITY_PUBLIC
        vibration: true,
      });

      await PushNotifications.createChannel({
        id: PUSH_CUSTOM_SOUND_CHANNEL_ID,
        name: 'Camera notifications with custom sound',
        description: 'New images and alerts from your cameras using the custom sound',
        importance: 4, // IMPORTANCE_HIGH — heads-up display
        sound: 'secacam',
        visibility: 1, // VISIBILITY_PUBLIC
        vibration: true,
      });
    } catch (error) {
      // A channel's importance is immutable after creation, so re-creating an existing
      // one can throw. Never let that block registration.
      this.log.warn('createChannel failed', { error });
    }
  }
}
